import axios, { InternalAxiosRequestConfig } from 'axios'
import { supabase } from '../supabase'

import { UserResponse } from './generated/models'

export type User = UserResponse

export interface SupabaseUser extends User {
  app_metadata: {
    provider?: string
    [key: string]: any
  }
  user_metadata: {
    [key: string]: any
  }
  created_at: string
  updated_at: string
  phone?: string | null
  confirmed_at?: string | null
  email_confirmed_at?: string | null
  banned_until?: string | null
  aud: string
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8080',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Configure axios instance for all API calls
api.interceptors.request.use(async (config) => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Check if this is during TOTP verification phase
      // If user is authenticated but TOTP not verified, this 401 is expected
      const {
        data: { session },
      } = await supabase.auth.getSession()
      const isLoggedInButNotMFAVerified = !!session && window.location.hash.includes('/auth/totp-verify')

      if (isLoggedInButNotMFAVerified) {
        console.log('[API] 401 during TOTP verification - this is expected, not signing out')
        return Promise.reject(error)
      }

      // Handle 401 Unauthorized responses by redirecting to login
      console.log('[API] 401 Unauthorized detected, redirecting to login page')

      // Clear the session from Supabase
      try {
        await supabase.auth.signOut()
      } catch (signOutError) {
        console.error('[API] Error during signOut:', signOutError)
      }

      // Redirect to login page using hash routing
      window.location.hash = '/auth/login'

      return Promise.reject(error)
    }

    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      // Trigger a session refresh to update ban status
      const auth = (window as any).__SUPABASE_AUTH_PROVIDER__
      if (auth?.checkBanStatus) {
        await auth.checkBanStatus()
      }
    }
    return Promise.reject(error)
  },
)

export const getPlatformAdminUsers = async (): Promise<User[]> => {
  const response = await api.get('/api/v1/users?role=platform_admin')
  return response.data.items
}

export const getPartnerUsers = async (partnerId: string): Promise<User[]> => {
  const response = await api.get(`/api/v1/users?entityId=${partnerId}`)
  return response.data.items
}

export const getUsers = async (): Promise<User[]> => {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token

  if (!token) {
    throw new Error('No authentication token available')
  }

  const response = await axios.get(`${import.meta.env.VITE_SUPABASE_URL}/auth/v1/admin/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  })

  return response.data
}

export const getUserProfile = async () => {
  const response = await api.get('/api/v1/users/profile')
  return response.data
}

export const deleteTotpDevice = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('No authenticated user')
  await api.delete(`/api/v1/users/${user.id}/totp`)
}

export const inviteUser = async (data: {
  email: string
  firstName: string
  lastName: string
  role?: string
  entityType?: string
  entityId?: string
}): Promise<void> => {
  await api.post('/api/v1/users', data)
}

export const deleteUser = async (userId: string): Promise<void> => {
  await api.delete(`/api/v1/users/${userId}`)
}

export const getUserById = async (userId: string): Promise<User> => {
  const response = await api.get(`/api/v1/users/${userId}`)
  return response.data
}

export const resendInvite = async (userId: string): Promise<void> => {
  await api.post(`/api/v1/users/${userId}/resend-invite`)
}

export { api }
